import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import whiteSend from "./whiteSend.png"
import yellowSend from "./purpleSend.svg"
import lightBulb from "./bulb.png"

const PREDEFINED_QA = {
  "Can I use my bus pass as ID?":
    "That document isn't accepted for legal ID checks. Please upload a valid passport, UK photocard driving licence, or national ID card.",
  "I don't have a passport or driving licence. What can I do?":
    "If you don't have one of the accepted documents, please contact your legal representative. They'll advise on alternative methods or arrange an in-branch verification.",
  "My ID has expired. Can I still use it?":
    "Unfortunately, expired documents cannot be accepted for legal ID checks. Please upload a valid, in-date passport, UK photocard driving licence, or national ID card.",
  "What counts as proof of address?":
    "If requested, you can upload a utility bill, bank statement, council tax letter, or HMRC notice dated within the last 3 months.",
  "Why am I being asked for Source of Funds?":
    "For certain transactions (like buying or remortgaging a property), we must confirm how the funds are being provided — e.g., savings, sale of property, gift from family. This helps protect against money laundering and is required by law.",
  "I've been asked to upload a document again — why?":
    "This usually happens when the image is blurry, has glare, or is missing part of the document. Please try again in good lighting and ensure all edges are visible.",
  "What happens if I can't complete the check right now?":
    "No problem. You can save your progress and we'll send you a secure link so you can return and complete it later.",
};

export function ChatInput({
  chatMessage,
  setChatMessage,
  onSubmit,
  currentStage,
  stageBeingProcessed,
  suggestedTopics,
  isProcessing,
  onPredefinedTopicClick,
  disablePrompt,
  consentGiven = false,
}) {
  const textareaRef = useRef(null);
  const [showFAQs, setShowFAQs] = useState(false);

  // Single useEffect for textarea height adjustment
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '42px'; // Reset to minimum
      textarea.style.overflowY = 'hidden';

      // Expand height
      textarea.style.height = `${textarea.scrollHeight}px`;

      // If it goes beyond 140px, cap and allow scroll
      if (textarea.scrollHeight > 140) {
        textarea.style.height = '140px';
        textarea.style.overflowY = 'auto';
      }
    }
  }, [chatMessage]);

  // Handle input change
  const handleInputChange = (e) => {
    setChatMessage(e.target.value);
  };

  // Handle Enter key submission
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatMessage.trim() && !isProcessing && !disablePrompt) {
        onSubmit(e);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (chatMessage.trim() && !isProcessing && !disablePrompt) {
      onSubmit(e);
    }
  };

  const placeholderText = "Type a message...";

  const handleTopicClick = (topic) => {
    if (stageBeingProcessed.current !== null || disablePrompt) {
      return;
    }

    if (PREDEFINED_QA[topic] && onPredefinedTopicClick) {
      onPredefinedTopicClick(topic, true); // Add true flag for suggested topic
      return;
    }

    setChatMessage(topic);
    setTimeout(() => {
      const event = new Event('submit');
      handleSubmit(event);
    }, 100);
  };

  return (
    <div className="input-chat">
      <div className="flex flex-col space-y-2 ">
        {/* Suggested Topics */}
        <div className="w-full max-w-2xl mx-auto">
          <div
            onClick={() => setShowFAQs(!showFAQs)}
            className="flex items-center gap-2 mb-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-6 h-6  flex items-center justify-center">
              <span className="text-white text-sm"><img src={lightBulb}/></span>
            </div>
            <h3 className="text-lg font-medium text-gray-800">FAQs</h3>
            <span className="text-sm text-gray-500 ml-auto">
              {showFAQs ? "▲" : "▶"}
            </span>
          </div>
          {showFAQs && (
            <div className="space-y-2">
              {suggestedTopics.map((topic, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 bg-[#D6CFDD] rounded-[16px] hover:bg-[#D6CFDD] transition-colors"
                >
                  <span className="text-[#243D3D] text-sm flex-1 pr-3">
                    {topic}
                  </span>
                  <button
                    type="button"
                    disabled={disablePrompt}
                    onClick={() => {
                      handleTopicClick(topic);
                      setShowFAQs(!showFAQs);
                    }}
                    className="hover:opacity-70 transition-opacity disabled:opacity-50"
                  >
                    <img src={yellowSend} className="h-[16px] w-[15px] text-[#D6CFDD]" />
                  </button>
                </div>
              
              ))}
            </div>
          )}
        </div>

        {/* Chat Input Form */}
        <form onSubmit={handleSubmit} className="flex items-end space-x-2 mb-3 items-center">
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              placeholder={placeholderText}
              value={chatMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="p-4 placeholder-[#243D3D] resize-none text-sm leading-relaxed text-[#243D3D] border-0 bg-[#d7efef] w-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-600"
              disabled={disablePrompt}
              style={{
                borderRadius:"16px",
                minHeight: '42px',
                maxHeight: '500px',
              }}
            />
            
          </div>
          <Button
            type="submit"
            size="icon"
            disabled={disablePrompt}
            style={{ borderRadius: "20px" }}
            className="mb-0 h-[42px] w-[42px] flex-shrink-0 text-[#1e1e1e] border-0 bg-[#2d5758] hover:bg-[#2d5758]/90 disabled:opacity-50"
          >
            <img src={whiteSend} className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}